import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { ResidencyAnswer } from '@/hooks/useResidency';
import OptionCard from './OptionCard';
import ResidencyShell from './ResidencyShell';
import ZipField from './ZipField';

/** What the backend accepts, mirrored so Continue can't offer a doomed POST. */
const FIVE_DIGITS = /^\d{5}$/;

interface ResidencyQuestionProps {
  /** A previously stored answer, so returning to the screen keeps the choice. */
  initialAnswer: ResidencyAnswer | null;
  onSubmit: (answer: ResidencyAnswer) => void;
  /** True when the lead has no ZIP stored yet, so this screen collects one. */
  askZip?: boolean;
  zip?: string;
  onZipChange?: (value: string) => void;
  /** The backend's rejection of the ZIP, if it rejected one. */
  zipError?: string | null;
  /** Held while the status is still loading or a save is in flight — either
   *  way the screen doesn't yet know enough to let the candidate commit. */
  busy?: boolean;
}

export default function ResidencyQuestion({
  initialAnswer,
  onSubmit,
  askZip = false,
  zip = '',
  onZipChange,
  zipError = null,
  busy = false,
}: ResidencyQuestionProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<ResidencyAnswer | null>(
    initialAnswer
  );

  const zipReady = !askZip || FIVE_DIGITS.test(zip);
  const canContinue = !!selected && zipReady && !busy;

  const handleContinue = () => {
    if (selected && canContinue) {
      onSubmit(selected);
    }
  };

  return (
    <ResidencyShell
      title={t('residency.title')}
      footer={
        <button
          type="button"
          disabled={!canContinue}
          onClick={handleContinue}
          className={`h-12 w-full max-w-[332px] rounded-[100px] text-base font-bold uppercase ${
            canContinue
              ? 'bg-[#569DE5] text-white'
              : 'cursor-not-allowed bg-[#eceef1] text-[#adb2ba]'
          }`}
        >
          {t('residency.continue')}
        </button>
      }
    >
      <div className="w-full max-w-[336px]">
        {/* Both paragraphs sit inside the described-by wrapper, so a screen
            reader announces the restriction and the ask, not just the first
            sentence. */}
        <div
          id="residency-description"
          className="text-center text-[15px] leading-[1.5] text-[#4a4e56]"
        >
          <p>{t('residency.description')}</p>
          <p className="mt-3">{t('residency.description_confirm')}</p>
        </div>

        <div
          role="radiogroup"
          aria-label={t('residency.title')}
          aria-describedby="residency-description"
          className="mt-[22px] flex flex-col gap-3"
        >
          <OptionCard
            testId="option-not-resident"
            selected={selected === 'other'}
            onSelect={() => setSelected('other')}
          >
            <Trans
              i18nKey="residency.option_not_resident"
              components={{ b: <b className="font-bold text-[#c0392b]" /> }}
            />
          </OptionCard>

          <OptionCard
            testId="option-resident"
            selected={selected === 'ca_il'}
            onSelect={() => setSelected('ca_il')}
          >
            {t('residency.option_resident')}
          </OptionCard>
        </div>

        {askZip && (
          <ZipField
            value={zip}
            onChange={(value) => onZipChange?.(value)}
            error={zipError}
            disabled={busy}
          />
        )}
      </div>
    </ResidencyShell>
  );
}
